import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AttributePill } from './pills';
import { ValueSheet, ValueSheetOption } from './ValueSheet';
import { ConfirmDialog } from './ConfirmDialog';
import { useWriteFeedback } from './useWriteFeedback';
import { optClasses } from './optionColor';

export interface CompanyDetail {
  id: string;
  name: string;
  website: string | null;
  industry: string | null;
  location: string | null;
  ownerId: string | null;
  ownerName: string | null;
  primaryContactId: string | null;
  primaryContactName: string | null;
  totalDealValue: number;
  contacts: Array<{ id: string; name: string | null; email: string | null; jobTitle: string | null }>;
  deals: Array<{ id: string; name: string; value: number | null; stageName: string | null }>;
  tasks: Array<{ id: string; title: string; status: string; completedAt: string | null }>;
  recentActivity: Array<{ id: string; type: string; summary: string; occurredAt: string }>;
  /** B9 — tickets are DETACHED by the delete and were disclosed nowhere. The count comes with the
   *  detail because a dialog can only state what the panel was given. */
  ticketCount: number;
  /** B9 — activities are unlinked by the delete and were disclosed NOWHERE. */
  activityCount: number;
  /** B9 — what would REFUSE the delete, known before the button is offered. */
  settledDealCount: number;
  invoiceCount: number;
}

// #50 (cmdr/DevOps, 2026-08-23): same converged fix as CustomerDetails/TaskDetailPanel — `name` empties
// to `''` (not a guessed real name); the render must fall back to '—' where it's displayed, never show
// the string blank-and-unlabelled.
function emptyCompany(id: string): CompanyDetail {
  return { id, name: '', website: null, industry: null, location: null, ownerId: null, ownerName: null, primaryContactId: null, primaryContactName: null, totalDealValue: 0, contacts: [], deals: [], tasks: [], recentActivity: [], ticketCount: 0, activityCount: 0, settledDealCount: 0, invoiceCount: 0 };
}

/**
 * AccountDetailPanel — #62 Accounts depth spec §4, applying the Tasks-loop shape (cmdr, 2026-08-23).
 * ONE surface for create AND detail/edit, same "create and detail cannot drift apart" reasoning as
 * TaskDetailPanel (Accounts has no ratified precedent like CustomerDetails to reuse instead, so this
 * IS the first detail panel for Company).
 *
 * DELETE is PM's ratified safety model: block-by-default + orphan-offered-in-the-same-dialog, cascade
 * NEVER built — a Contact is a Lead carrying conversation history, so this can only ever unlink, never
 * cascade-destroy. The confirm shows REAL counts (contacts/deals already loaded in this same panel —
 * cross-cutting rule 2: "Copy states a real COUNT, never a category").
 */
export const AccountDetailPanel: React.FC<{
  companyId: string | 'new';
  /** D-C6 — the one panel in the suite that never received the prop and hardcoded 'popover', so on
   *  a phone the owner picker anchored a 256px popover to a 24px pill instead of the ratified sheet. */
  sheetShape: 'sheet' | 'popover';
  onClose: () => void;
  onCreated: (company: CompanyDetail) => void;
  onUpdated: (company: CompanyDetail) => void;
  onDeleted: (companyId: string, unlinked: { contacts: number; deals: number; tasks: number; tickets: number; activities: number }) => void;
}> = ({ companyId, sheetShape, onClose, onCreated, onUpdated, onDeleted }) => {
  const { t } = useTranslation('common');
  const [company, setCompany] = useState<CompanyDetail | null>(null);
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>(companyId === 'new' ? 'ready' : 'loading');
  const [createName, setCreateName] = useState('');
  // A3 (design §4.3) — see the TicketDetailPanel twin for the three-outcome rationale.
  const feedback = useWriteFeedback();
  const [createError, setCreateError] = useState(false);
  const [pendingCreatedId, setPendingCreatedId] = useState<string | null>(null);
  const [editingField, setEditingField] = useState<'name' | 'website' | 'industry' | 'location' | null>(null);
  const [fieldDraft, setFieldDraft] = useState('');
  const [members, setMembers] = useState<{ id: string; name: string | null }[]>([]);
  const [editingPicker, setEditingPicker] = useState<{ field: 'owner' | 'primaryContact'; anchor: HTMLElement } | null>(null);
  const hasLoadedRef = useRef(false);
  // B9 — the refusal's REASON, not only its number. An invoice block and a settled-deal block are
  // different facts about the company and must not render the same sentence.
  const [deleteBlocked, setDeleteBlocked] = useState<{ reason: 'invoices' | 'settledDeals'; count: number } | null>(null);

  // #50 (cmdr/DevOps, 2026-08-23): same converged fix as CustomerDetails/TaskDetailPanel. On a real
  // companyId CHANGE (not the true first mount), reset synchronously to an honest empty shell — never
  // stale-and-scrimmed, never null. `company` stays truthy the whole revalidating window so the render
  // never structurally unmounts (no detach — invariant 1), while every field inside reads as genuinely
  // absent, so nothing wrong is ever visible (no wrong-entity-flash — invariant 2). A revalidation error
  // does NOT null `company` — the shell (or the prior real value, on true-first-mount failure) stays put
  // and `account-detail-error` renders alongside it.
  useEffect(() => {
    if (companyId === 'new') return;
    // 🔴 root fix (cmdr/DevOps, 2026-08-25 — same defect found+fixed in InvoiceDetailPanel, applied
    // here on the identical pattern): right after create, the parent flips companyId from 'new' to the
    // real id on the SAME panel instance — this effect re-fires, but `company` here already holds fresh
    // data for that exact id. Re-fetching anyway forces a real `loadState:'loading'` window, and every
    // inline-edit click handler gates on `!isLoading` — a click landing in that window is SILENTLY
    // SWALLOWED. Skip the redundant round-trip when we already have this exact record.
    if (company && company.id === companyId) { hasLoadedRef.current = true; return; }
    setLoadState('loading');
    if (hasLoadedRef.current) setCompany(emptyCompany(companyId));
    fetch(`/api/companies/${companyId}`).then((r) => r.ok ? r.json() : Promise.reject(r.status)).then((d: CompanyDetail) => {
      setCompany(d); setLoadState('ready'); hasLoadedRef.current = true;
    }).catch(() => { setLoadState('error'); });
  }, [companyId]);

  useEffect(() => {
    fetch('/api/tasks/assignees').then((r) => r.ok ? r.json() : []).then(setMembers).catch(() => {});
  }, []);

  const submitCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = createName.trim();
    if (!name) return;
    const res = await fetch('/api/companies', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name }) }).catch(() => null);
    // D-C4: a failed create is a SAVE error, never the load-error string — and the typed name stays.
    if (!res?.ok) { setCreateError(true); return; }
    setCreateError(false);
    const { companyId: newId } = await res.json();
    // D-C4's "secondary": the follow-up GET was unguarded — a failure there threw unhandled and left
    // the create form up with the company ALREADY created (a resubmit = duplicate). Now: re-check.
    const ok = await confirmCreated(newId);
    if (!ok) setPendingCreatedId(newId);
  };

  const confirmCreated = async (newId: string): Promise<boolean> => {
    const detailRes = await fetch(`/api/companies/${newId}`).catch(() => null);
    if (!detailRes?.ok) return false;
    const detail: CompanyDetail = await detailRes.json();
    setPendingCreatedId(null);
    setCompany(detail);
    onCreated(detail);
    return true;
  };

  const patch = async (data: Partial<{ name: string; website: string | null; industry: string | null; location: string | null; ownerId: string | null; primaryContactId: string | null }>): Promise<boolean> => {
    if (!company) return false;
    const res = await fetch(`/api/companies/${company.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }).catch(() => null);
    // D-C5 — the silent `return` here was the exemplar's silent-data-loss defect: the input had
    // already closed, the field snapped back, and the operator read their vanished text as saved.
    if (!res?.ok) { feedback.fail(t('writeStates.saveFailed')); return false; }
    const updated: CompanyDetail = await res.json();
    setCompany(updated);
    onUpdated(updated);
    return true;
  };

  /** D-C7 — a picker apply is an optimistic-class write on a touch surface; UndoToast's own contract
   *  calls the undo REQUIRED. Success offers a working undo (the inverse PATCH through the same
   *  path); failure is patch()'s visible error. */
  const applyPicker = async (data: { ownerId: string | null } | { primaryContactId: string | null }, prev: typeof data) => {
    const ok = await patch(data);
    if (ok) feedback.undoable(t('writeStates.updated'), () => { void patch(prev); });
  };

  // The confirm is the ratified ConfirmDialog, never window.confirm (P7 / D-C8): a native confirm's
  // BUTTONS render in the browser's language while the message goes through t() — one dialog, two
  // languages — and on a phone it is an OS alert, not the ratified sheet. The message keeps its real
  // counts (X-5), and at zero the counts clause never renders.
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const handleDelete = async () => {
    if (!company) return;
    setConfirmingDelete(false);
    setDeleteBlocked(null);
    const res = await fetch(`/api/companies/${company.id}`, { method: 'DELETE' });
    if (res.status === 409) {
      const body = await res.json();
      // Settled deals first: it is the refusal that protects a RECORD, and if a company somehow
      // hit both the money-attribution one is the more important thing to say.
      if (body.settledDealCount) setDeleteBlocked({ reason: 'settledDeals', count: body.settledDealCount });
      else setDeleteBlocked({ reason: 'invoices', count: body.invoiceCount ?? 0 });
      return;
    }
    if (!res.ok) { feedback.fail(t('writeStates.actionFailed')); return; }
    const { unlinked } = await res.json();
    onDeleted(company.id, unlinked);
    onClose();
  };

  // B9 — what the delete ALSO detaches, as real counts, built the same way the survivor toast
  // builds its parts. Zero-count relations are omitted rather than rendered as "0", so the
  // disclosure can never overstate; and it is derived from the panel's own loaded data, so it
  // cannot drift from what the user is looking at.
  const detachedAlso: string[] = [
    (company?.tasks.length ?? 0) > 0 ? t('accountsGrid.detachedTasks', { count: company!.tasks.length }) : null,
    (company?.ticketCount ?? 0) > 0 ? t('accountsGrid.detachedTickets', { count: company!.ticketCount }) : null,
    (company?.activityCount ?? 0) > 0 ? t('accountsGrid.detachedActivities', { count: company!.activityCount }) : null,
  ].filter((x): x is string => x !== null);

  // 🔴 THE DELETE IS REFUSED BEFORE IT IS OFFERED, not after it is attempted. Settled revenue and
  // invoices both refuse at the service; knowing that here means the dialog can explain instead of
  // offering a button that is going to fail. Settled deals are named FIRST when both apply: it is
  // the refusal that protects a record rather than a workflow.
  const deleteRefusal: { reason: 'settledDeals' | 'invoices'; count: number } | null =
    (company?.settledDealCount ?? 0) > 0 ? { reason: 'settledDeals', count: company!.settledDealCount }
    : (company?.invoiceCount ?? 0) > 0 ? { reason: 'invoices', count: company!.invoiceCount }
    : null;

  const memberOptions: ValueSheetOption[] = members.map((m) => ({ value: m.id, label: m.name ?? '—' }));
  const contactOptions: ValueSheetOption[] = (company?.contacts ?? []).map((c) => ({ value: c.id, label: c.name ?? '—' }));
  const roseFg = optClasses('rose').fg;

  const startEdit = (field: typeof editingField, current: string | null) => { setFieldDraft(current ?? ''); setEditingField(field); };
  const saveEdit = () => {
    if (!editingField || !company) return;
    const f = editingField; setEditingField(null);
    const orig = f === 'name' ? company.name : f === 'website' ? company.website : f === 'industry' ? company.industry : company.location;
    if (fieldDraft === (orig ?? '')) return;
    if (f === 'name' && !fieldDraft.trim()) return;
    // On failure the field RE-OPENS with the draft intact (fieldDraft survives the close) — the
    // typed text must never vanish and read as saved (D-C5's AC: error present AND draft retained).
    void patch({ [f]: fieldDraft.trim() || null } as any).then((ok) => { if (!ok) setEditingField(f); });
  };

  return (
    <div className="flex flex-col h-full w-full overflow-y-auto bg-canvas" data-testid="account-detail-panel">
      <button data-testid="account-detail-back" className="lg:hidden self-start flex items-center gap-1 px-4 py-3 min-h-[44px] text-sm text-primary font-semibold"
        onClick={onClose}>&larr; {t('accountsGrid.backToGrid')}</button>

      <div className="flex flex-col gap-4 p-4 pb-16 lg:pb-4">
        {companyId === 'new' && !company && !pendingCreatedId && (
          <form data-testid="account-create-form" onSubmit={submitCreate} className="rounded-xl border border-border-neutral bg-surface p-4 flex flex-col gap-3">
            <input autoFocus data-testid="account-create-name" value={createName} onChange={(e) => setCreateName(e.target.value)}
              placeholder={t('accountsGrid.columns.name')} className="h-10 px-3 rounded-lg border border-border-neutral text-sm font-semibold" dir="auto" />
            {createError && <div className="text-xs text-opt-rose" data-testid="account-create-error">{t('writeStates.createFailed')}</div>}
            <button type="submit" disabled={!createName.trim()} data-testid="account-create-submit"
              className="h-10 px-4 rounded-lg bg-primary text-white text-sm font-semibold disabled:opacity-50 self-start">
              {t('accountsGrid.create')}
            </button>
          </form>
        )}

        {pendingCreatedId && (
          <div className="p-6 text-center text-sm text-text-muted flex flex-col items-center gap-3" data-testid="account-create-unconfirmed">
            {t('writeStates.confirmFailed')}
            <button type="button" data-testid="account-create-unconfirmed-retry" onClick={() => void confirmCreated(pendingCreatedId)}
              className="min-h-[44px] px-4 rounded-lg border border-border-neutral text-sm font-semibold text-text-strong hover:bg-canvas">
              {t('writeStates.recheck')}
            </button>
          </div>
        )}

        {companyId !== 'new' && loadState === 'loading' && !company && <div className="p-6 text-center text-sm text-text-muted">…</div>}
        {loadState === 'error' && !company && <div className="p-6 text-center text-sm text-text-muted" data-testid="account-detail-error">{t('customers.detail.loadError')}</div>}

        {company && (
          <div data-testid="account-detail-content" aria-busy={loadState === 'loading'}>
            {loadState === 'loading' && (
              <div data-testid="account-detail-revalidating" className="text-xs text-text-muted text-center py-1" aria-live="polite">{t('customers.detail.loading')}</div>
            )}
            {/* identity — inline edit */}
            <div className="rounded-xl border border-border-neutral bg-surface p-4">
              {editingField === 'name' ? (
                <input autoFocus data-testid="account-name-input" value={fieldDraft} onChange={(e) => setFieldDraft(e.target.value)}
                  onBlur={saveEdit} onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setEditingField(null); }}
                  className="w-full h-9 px-2 -mx-2 rounded-lg border border-primary text-lg font-bold" dir="auto" />
              ) : (
                <h2 data-testid="account-name" className="text-lg font-bold text-text-strong truncate cursor-text" onClick={() => { if (loadState !== 'loading') startEdit('name', company.name); }}>{company.name || <span className="text-text-muted/70">—</span>}</h2>
              )}
            </div>

            {/* profile — inline edit for website/industry/location */}
            <div className="rounded-xl border border-border-neutral bg-surface p-4 flex flex-col gap-2">
              {(['website', 'industry', 'location'] as const).map((f) => (
                <div key={f} className="text-xs text-text-muted flex flex-col gap-1">
                  {t(`accountsGrid.columns.${f}`)}
                  {editingField === f ? (
                    <input autoFocus data-testid={`account-${f}-input`} value={fieldDraft} onChange={(e) => setFieldDraft(e.target.value)}
                      onBlur={saveEdit} onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setEditingField(null); }}
                      className="h-9 px-2 rounded-lg border border-primary text-sm text-text-strong" dir="auto" />
                  ) : (
                    <div data-testid={`account-${f}`} className="text-sm text-text-strong cursor-text min-h-[20px]" onClick={() => { if (loadState !== 'loading') startEdit(f, company[f]); }}>
                      {company[f] || <span className="text-text-muted/70">—</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* owner / primary contact — ValueSheet pickers */}
            <div className="rounded-xl border border-border-neutral bg-surface p-4 flex items-center gap-2 flex-wrap">
              <AttributePill value={company.ownerName} label={t('accountsGrid.owner')}
                emptyText={t('customers.detail.setField', { field: t('accountsGrid.owner') })}
                onOpen={(a) => { if (loadState !== 'loading') setEditingPicker({ field: 'owner', anchor: a }); }} />
              <AttributePill value={company.primaryContactName} label={t('accountsGrid.columns.primaryContact')}
                emptyText={t('customers.detail.setField', { field: t('accountsGrid.columns.primaryContact') })}
                onOpen={(a) => { if (loadState !== 'loading') setEditingPicker({ field: 'primaryContact', anchor: a }); }} />
            </div>

            {/* relations — display-only this round (no create-from-here picker yet) */}
            <div className="rounded-xl border border-border-neutral bg-surface p-4">
              <div className="text-[11px] font-semibold text-text-muted uppercase tracking-wide mb-2">{t('accountsGrid.columns.totalDealValue')}</div>
              <div data-testid="account-total-deal-value" className="text-lg font-bold text-text-strong">{company.totalDealValue.toLocaleString()}</div>
            </div>

            <div className="rounded-xl border border-border-neutral bg-surface p-4">
              <div className="text-[11px] font-semibold text-text-muted uppercase tracking-wide mb-2">{t('accountsGrid.contactsAtCompany')}</div>
              {company.contacts.length > 0 ? (
                <div className="space-y-1.5" data-testid="account-contacts-list">
                  {company.contacts.map((c) => <div key={c.id} className="text-sm text-text-strong truncate">{c.name || '—'}</div>)}
                </div>
              ) : <span className="text-text-muted italic text-sm">{t('accountsGrid.noContacts')}</span>}
            </div>

            <div className="rounded-xl border border-border-neutral bg-surface p-4">
              <div className="text-[11px] font-semibold text-text-muted uppercase tracking-wide mb-2">{t('customers.detail.deals')}</div>
              {company.deals.length > 0 ? (
                <div className="space-y-1.5" data-testid="account-deals-list">
                  {company.deals.map((d) => (
                    <div key={d.id} className="flex items-center justify-between text-sm gap-2">
                      <span className="truncate text-text-strong">{d.name}</span>
                      <span className="text-xs text-text-muted shrink-0">{d.stageName ?? '—'}</span>
                    </div>
                  ))}
                </div>
              ) : <span className="text-text-muted italic text-sm">{t('customers.detail.noDeals')}</span>}
            </div>

            <div className="rounded-xl border border-border-neutral bg-surface p-4">
              <div className="text-[11px] font-semibold text-text-muted uppercase tracking-wide mb-2">{t('customers.detail.tasks')}</div>
              {company.tasks.length > 0 ? (
                <div className="space-y-1.5" data-testid="account-tasks-list">
                  {company.tasks.map((tk) => <div key={tk.id} className={`text-sm truncate ${tk.completedAt ? 'line-through text-text-muted' : 'text-text-strong'}`}>{tk.title}</div>)}
                </div>
              ) : <span className="text-text-muted italic text-sm">{t('customers.detail.noTasks')}</span>}
            </div>

            <div className="rounded-xl border border-border-neutral bg-surface p-4">
              <div className="text-[11px] font-semibold text-text-muted uppercase tracking-wide mb-2">{t('customers.detail.recordHistory')}</div>
              {company.recentActivity.length > 0 ? (
                <div className="space-y-2" data-testid="account-timeline-list">
                  {company.recentActivity.map((a) => <div key={a.id} className="text-sm text-text-strong">{a.summary}</div>)}
                </div>
              ) : <span className="text-text-muted italic text-sm">{t('customers.detail.noChangesYet')}</span>}
            </div>

            {/* delete — separated, text-only, rose. Real counts already loaded above (contacts/deals),
                cascade never built — the confirm message states them, never a filled button. Invoices
                are a real BLOCK (not offered here at all), since there's no meaningful "orphan" state
                for a required companyId — server returns 409 + a real count, disclosed below.
                B9 adds a SECOND block on the same channel: a closed deal's companyId is its settled
                revenue attribution (B1's pin), so a company that owns won/lost revenue cannot be
                deleted out from under it either. Two blocks, two different sentences — the banner
                below picks by reason, never by whichever count happens to be set. */}
            <div className="pt-2 mt-2 border-t border-border-neutral">
              {deleteBlocked !== null && (
                <div className="text-xs text-opt-rose mb-2" data-testid="account-delete-blocked">
                  {deleteBlocked.reason === 'settledDeals'
                    ? t('accountsGrid.deleteBlockedBySettledDeals', { count: deleteBlocked.count })
                    : t('accountsGrid.deleteBlockedByInvoices', { count: deleteBlocked.count })}
                </div>
              )}
              <button type="button" data-testid="account-delete" onClick={() => setConfirmingDelete(true)} disabled={loadState === 'loading'}
                className={`text-sm font-semibold min-h-[44px] px-1 disabled:opacity-50 ${roseFg}`}>
                {t('accountsGrid.delete')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 🔴 BLOCKED MODE. A destructive button on an operation that will ALWAYS be refused is the
          ceremony problem in its purest form: the dialog offers an action it knows it cannot
          perform, and the user learns the truth afterwards from a banner. Design 03 §A1 argued
          this exact case — "an explanation with a destructive button says one thing and offers
          another" — and ConfirmDialog has carried a `blocked` mode ever since; nobody had wired it.
          Ruled by cmdr 2026-09-11. The post-attempt banner below stays: it is the backstop for a
          refusal that appears BETWEEN the panel load and the click (someone closes a deal while
          the dialog is open), which the pre-click check cannot see. */}
      {confirmingDelete && company && (
        deleteRefusal !== null ? (
          <ConfirmDialog blocked
            message={deleteRefusal.reason === 'settledDeals'
              ? t('accountsGrid.deleteBlockedBySettledDeals', { count: deleteRefusal.count })
              : t('accountsGrid.deleteBlockedByInvoices', { count: deleteRefusal.count })}
            onClose={() => setConfirmingDelete(false)} />
        ) : (
          <ConfirmDialog destructive
            message={[
              (company.contacts.length + company.deals.length) > 0
                ? t('accountsGrid.deleteConfirmWithCounts', { contacts: company.contacts.length, deals: company.deals.length, name: company.name })
                : t('accountsGrid.deleteConfirm', { name: company.name }),
              detachedAlso.length > 0 ? t('accountsGrid.deleteAlsoDetaches', { parts: detachedAlso.join(', ') }) : null,
            ].filter(Boolean).join(' ')}
            onConfirm={() => void handleDelete()}
            onClose={() => setConfirmingDelete(false)} />
        )
      )}
      {editingPicker?.field === 'owner' && company && (
        <ValueSheet shape={sheetShape} title={`${t('accountsGrid.owner')} — ${company.name}`} options={memberOptions} current={company.ownerId}
          anchor={editingPicker.anchor} onSelect={(v) => { setEditingPicker(null); void applyPicker({ ownerId: v }, { ownerId: company.ownerId }); }} onClose={() => setEditingPicker(null)} />
      )}
      {editingPicker?.field === 'primaryContact' && company && (
        <ValueSheet shape={sheetShape} title={`${t('accountsGrid.columns.primaryContact')} — ${company.name}`} options={contactOptions} current={company.primaryContactId}
          anchor={editingPicker.anchor} onSelect={(v) => { setEditingPicker(null); void applyPicker({ primaryContactId: v }, { primaryContactId: company.primaryContactId }); }} onClose={() => setEditingPicker(null)} />
      )}
      {feedback.node}
    </div>
  );
};
